import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "./ui/button";
import Terminal from "./Terminal";
import { useTheme } from "../hooks/useTheme";
import { cn } from "../lib/utils";

const navLinks = [
    { to: "/", label: "Home" },
    { to: "/projects", label: "Projects" },
    { to: "/experience", label: "Experience" },
    { to: "/education", label: "Education" },
    { to: "/aboutme", label: "About" },
];

function Navbar() {
    const [open, setOpen] = useState(false);
    const [terminalOpen, setTerminalOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setTerminalOpen((prev) => !prev);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    return (
        <header className="fixed inset-x-0 top-0 z-50 h-[var(--nav-height)] border-b bg-background/80 backdrop-blur-md">
            <nav className="mx-auto flex h-full max-w-5xl items-center justify-between px-6">
                <Link to="/" className="font-mono text-sm font-semibold tracking-tight text-foreground">
                    mauric10<span className="text-primary">.com</span>
                </Link>

                <div className="hidden items-center gap-1 md:flex">
                    {navLinks.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            className={({ isActive }) =>
                                cn(
                                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                                    isActive
                                        ? "bg-accent text-accent-foreground"
                                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                                )
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="font-mono text-xs text-muted-foreground"
                        onClick={() => setTerminalOpen(true)}
                        aria-label="Open terminal"
                    >
                        {">_"}
                        <kbd className="hidden rounded border bg-muted px-1 py-0.5 text-[10px] sm:inline-block">⌘K</kbd>
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {theme === "dark" ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setOpen((prev) => !prev)}
                        aria-expanded={open}
                        aria-label={open ? "Close navigation menu" : "Open navigation menu"}
                    >
                        {open ? <X /> : <Menu />}
                    </Button>
                </div>
            </nav>

            {open && (
                <div className="border-b bg-background md:hidden">
                    <div className="mx-auto flex max-w-5xl flex-col gap-1 px-6 py-4">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                className={({ isActive }) =>
                                    cn(
                                        "rounded-md px-3 py-2 text-sm font-medium",
                                        isActive
                                            ? "bg-accent text-accent-foreground"
                                            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                                    )
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}

            <Terminal open={terminalOpen} onOpenChange={setTerminalOpen} toggleTheme={toggleTheme} />
        </header>
    );
}

export default Navbar;
